/**
 * Media Service
 * Handles all media URL fetching and caching
 * Abstracts the storage backend from components
 */

// const API_BASE_URL = process.env.REACT_APP_MEDIA_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_MEDIA_API_URL || 'https://rosebornefilms-backend.onrender.com/api';


// In-memory cache for media URLs
const urlCache = new Map();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

class MediaService {
  /**
   * Get media URL with optional transformations
   * @param {string} classification - 'portraits', 'commercial', or 'assets'
   * @param {string} type - 'photo' or 'video'
   * @param {string} category - Category name
   * @param {string} id - Media ID
   * @param {object} options - Transformation options (width, quality, format, responsive)
   * @returns {Promise<string>} Media URL
   */
  static async getMediaUrl(classification, type, category, id, options = {}) {
    const cacheKey = `${classification}:${type}:${category}:${id}:${JSON.stringify(options)}`;

    // Check cache first
    if (urlCache.has(cacheKey)) {
      const cached = urlCache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.url;
      }
      urlCache.delete(cacheKey);
    }

    try {
      const queryParams = new URLSearchParams();
      if (options.width) queryParams.append('width', options.width);
      if (options.quality) queryParams.append('quality', options.quality);
      if (options.format) queryParams.append('format', options.format);
      if (options.responsive) queryParams.append('responsive', options.responsive);

      const url = `${API_BASE_URL}/media/${classification}/${type}/${category}/${id}/${queryParams.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch media URL: ${response.statusText}`);
      }

      const data = await response.json();
      const mediaUrl = data.url;

      // Cache the result
      urlCache.set(cacheKey, {
        url: mediaUrl,
        timestamp: Date.now(),
      });

      return mediaUrl;
    } catch (error) {
      console.error('MediaService Error:', error);
      // Fallback to local URL if API fails (for backward compatibility)
      return this.getFallbackUrl(type, category, id);
    }
  }

  /**
   * Get all media for a gallery category
   * @param {string} type - 'photography' or 'film'
   * @param {string} category - Category name
   * @returns {Promise<Array>} Array of media items
   */
  static async getGallery(classification, type, category) {
    const cacheKey = `gallery:${classification}:${type}:${category}`;

    if (urlCache.has(cacheKey)) {
      const cached = urlCache.get(cacheKey);
      if (Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
      }
      urlCache.delete(cacheKey);
    }

    try {
      const url = `${API_BASE_URL}/gallery/${classification}/${type}/${category}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch gallery: ${response.statusText}`);
      }

      const data = await response.json();
      const gallery = data.data || [];

      // Cache the result
      urlCache.set(cacheKey, {
        data: gallery,
        timestamp: Date.now(),
      });

      return gallery;
    } catch (error) {
      console.error('Gallery Fetch Error:', error);
      return [];
    }
  }

  /**
   * Clear cache
   */
  static clearCache(classification = null, type = null, category = null) {
    if (!classification && !type && !category ) {
      urlCache.clear();
      return;
    }

    const prefix = `${type}:${category}`;
    for (const key of urlCache.keys()) {
      if (key.startsWith(prefix)) {
        urlCache.delete(key);
      }
    }
  }

  /**
   * Fallback to local URL (development mode)
   */
  static getFallbackUrl(classification, type, category, id) {
    const folder = classification === 'portraits' ? 'portraits' : classification === 'commercial' ? 'commercial' : 'assets';
    return `/${folder}/${type}/${category}/${id}`;
  }

  /**
   * Health check API
   */
  static async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'offline', provider: 'local' };
    }
  }
}

export default MediaService;
